<script setup>
  import { toRefs } from 'vue';
  import { useAuth } from '../../../auth';
  const { user } = toRefs(useAuth());
</script>
<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <div class="page-header">
          <div class="page-title">
            <h4>Users</h4>
            <h6>Who can log in to this application</h6>
          </div>
          <div class="page-btn" v-if="isSuperAdmin(user)">
            <a href="javascript:void(0);" class="btn btn-added" @click="showForm = !showForm">
              {{ showForm ? 'Close' : 'Add User' }}
            </a>
          </div>
        </div>

        <div v-if="!isSuperAdmin(user)" class="card"><div class="card-body">Only super admins can manage users.</div></div>

        <template v-else>
          <!-- Add user -->
          <div v-if="showForm" class="card">
            <div class="card-body">
              <form class="row" @submit.prevent="createUser">
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group">
                    <label for="new-user-email">Email</label>
                    <input id="new-user-email" v-model.trim="form.email" type="email" class="form-control" required />
                  </div>
                </div>
                <div class="col-lg-3 col-sm-6 col-12">
                  <div class="form-group">
                    <label for="new-user-name">Name</label>
                    <input id="new-user-name" v-model.trim="form.name" type="text" class="form-control" />
                  </div>
                </div>
                <div class="col-lg-2 col-sm-6 col-12">
                  <div class="form-group">
                    <label for="new-user-role">Role</label>
                    <select id="new-user-role" v-model="form.role" class="form-control">
                      <option v-for="role in roles" :key="role" :value="role">{{ role }}</option>
                    </select>
                  </div>
                </div>
                <div class="col-lg-2 col-sm-6 col-12">
                  <div class="form-group">
                    <label for="new-user-password">Temporary password</label>
                    <input id="new-user-password" v-model="form.password" type="text" class="form-control"
                      autocomplete="off" required />
                  </div>
                </div>
                <div class="col-lg-2 col-sm-6 col-12 d-flex align-items-end">
                  <div class="form-group w-100">
                    <button type="submit" class="btn btn-submit w-100" :disabled="busy">Create</button>
                  </div>
                </div>
                <div class="col-12 text-muted">The user must change this password the first time they log in.</div>
              </form>
            </div>
          </div>

          <!-- Users -->
          <div class="card">
            <div class="card-body">
              <div class="table-responsive">
                <a-table :columns="columns" :data-source="users" row-key="id" :loading="loading">
                  <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'role'">
                      <select class="form-control form-control-sm" :value="record.role"
                        :disabled="record.id === user.attributes.sub"
                        @change="updateUser(record, { role: $event.target.value })">
                        <option v-for="role in rolesFor(record)" :key="role" :value="role">{{ role }}</option>
                      </select>
                    </template>
                    <template v-else-if="column.key === 'status'">
                      <span v-if="!record.is_active" class="badges bg-lightred">Disabled</span>
                      <span v-else-if="record.must_change_password" class="badges bg-lightyellow">Must set password</span>
                      <span v-else class="badges bg-lightgreen">Active</span>
                    </template>
                    <template v-else-if="column.key === 'last_login'">
                      {{ record.last_login ? new Date(record.last_login + 'Z').toLocaleString() : 'Never' }}
                    </template>
                    <template v-else-if="column.key === 'action'">
                      <a href="javascript:void(0);" class="me-3" @click="resetPassword(record)">Reset password</a>
                      <a v-if="record.id !== user.attributes.sub" href="javascript:void(0);"
                        @click="updateUser(record, { is_active: !record.is_active })">
                        {{ record.is_active ? 'Disable' : 'Enable' }}
                      </a>
                    </template>
                  </template>
                </a-table>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const ROLES = ['SuperAdmin', 'Admin', 'Issuer', 'Purchase', 'Readonly'];
const MIN_PASSWORD_LENGTH = 8;
const PATH = '/v1/users';

const emptyForm = () => ({ email: '', name: '', role: 'Readonly', password: '' });
const apiError = (error) => {
  const detail = error.response && error.response.data && error.response.data.detail;
  return typeof detail === 'string' ? detail : 'Something went wrong. Please try again.';
};

export default {
  data() {
    return {
      users: [],
      roles: ROLES,
      form: emptyForm(),
      showForm: false,
      loading: false,
      busy: false,
      columns: [
        { title: 'Email', dataIndex: 'email', key: 'email', sorter: (a, b) => a.email.localeCompare(b.email) },
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Role', key: 'role' },
        { title: 'Status', key: 'status' },
        { title: 'Last login', key: 'last_login' },
        { title: 'Action', key: 'action' },
      ],
    };
  },
  methods: {
    isSuperAdmin(user) {
      return user.attributes['custom:role'] === 'SuperAdmin';
    },
    rolesFor(record) {
      // keep showing a role migrated from Cognito even if it is not in the standard list
      return ROLES.includes(record.role) ? ROLES : [record.role, ...ROLES];
    },
    async loadUsers() {
      this.loading = true;
      try {
        const { data } = await axios.get(PATH);
        this.users = data;
      } catch (e) {
        this.$swal.fire('Could not load users', apiError(e), 'error');
      } finally {
        this.loading = false;
      }
    },
    async createUser() {
      if (this.form.password.length < MIN_PASSWORD_LENGTH) {
        this.$swal.fire('Password too short', `Use at least ${MIN_PASSWORD_LENGTH} characters.`, 'warning');
        return;
      }
      this.busy = true;
      try {
        await axios.post(PATH, this.form);
        this.$swal.fire('User created', `Give ${this.form.email} their temporary password.`, 'success');
        this.form = emptyForm();
        this.showForm = false;
        await this.loadUsers();
      } catch (e) {
        this.$swal.fire('Could not create user', apiError(e), 'error');
      } finally {
        this.busy = false;
      }
    },
    async updateUser(record, changes) {
      try {
        const { data } = await axios.put(`${PATH}/${record.id}`, changes);
        Object.assign(record, data);
      } catch (e) {
        this.$swal.fire('Could not update user', apiError(e), 'error');
        await this.loadUsers();
      }
    },
    async resetPassword(record) {
      const result = await this.$swal.fire({
        title: `Reset password for ${record.email}`,
        text: 'They will be logged out and must change it at next login.',
        input: 'text',
        inputLabel: 'Temporary password',
        showCancelButton: true,
        confirmButtonText: 'Reset',
        inputValidator: (value) => (!value || value.length < MIN_PASSWORD_LENGTH
          ? `Use at least ${MIN_PASSWORD_LENGTH} characters.` : undefined),
      });
      if (!result.isConfirmed) return;
      try {
        const { data } = await axios.post(`${PATH}/${record.id}/reset-password`, { password: result.value });
        Object.assign(record, data);
        this.$swal.fire('Password reset', `Give ${record.email} the new temporary password.`, 'success');
      } catch (e) {
        this.$swal.fire('Could not reset password', apiError(e), 'error');
      }
    },
  },
  mounted() {
    this.loadUsers();
  },
};
</script>
